/**
 * Authentification MSAL — tenant CIAM du studio Exidis (lot 2 de la spec
 * `docs/superpowers/specs/2026-09-14-auth-spa-api-ciam-design.md`, corrections de la revue
 * qualité/sécurité du même lot).
 *
 * Le jeton d'accès CIAM est géré par MSAL et vit en `sessionStorage` (D3) : exiPlan ne le
 * persiste jamais lui-même, ne le lit jamais (il est opaque côté client, § 3.2), et ne le
 * transmet qu'en en-tête `Authorization` (`lib/api.js`). Sans les trois variables
 * `VITE_CIAM_*`, `ciamConfigured` vaut `false` et ce module n'instancie rien : le serveur
 * retombe alors sur le bypass de développement ou le cookie hérité (coexistence D7,
 * `lib/connexion.js::startLegacyLogin`).
 */

import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

const TENANT_ID = import.meta.env.VITE_CIAM_TENANT_ID;
const CLIENT_ID = import.meta.env.VITE_CIAM_CLIENT_ID;
const SCOPE = import.meta.env.VITE_CIAM_SCOPE;

/** Configuration CIAM complète ? Sert de garde à tout ce fichier et à `SessionContext`. */
export const ciamConfigured = Boolean(TENANT_ID && CLIENT_ID && SCOPE);

const SCOPES = SCOPE ? [SCOPE] : [];

// ⚠️ CIAM : jamais `login.microsoftonline.com` pour ce tenant — l'autorité se résout par
// découverte sur `<tid>.ciamlogin.com` (§ 1 de la spec, piège déjà payé par exiWatch).
const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: TENANT_ID ? `https://${TENANT_ID}.ciamlogin.com/${TENANT_ID}` : undefined,
    knownAuthorities: TENANT_ID ? [`${TENANT_ID}.ciamlogin.com`] : [],
    redirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
    postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : undefined,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

/** `null` quand la configuration CIAM est absente — aucune instance MSAL n'est créée. */
export const msalInstance = ciamConfigured ? new PublicClientApplication(msalConfig) : null;

/**
 * Chemin relatif sûr uniquement — refuse toute valeur absolue (`https://…`), mais aussi
 * `//hôte/…` et `/\hôte/…` : deux formes qu'un navigateur peut encore résoudre comme une
 * redirection vers un autre hôte alors qu'elles commencent bien par `/` (audit sécurité,
 * revue du lot 2). Même garde que le flux legacy, § 3.2 « returnTo ».
 */
function sanitizeReturnTo(path) {
  if (typeof path !== 'string' || !path.startsWith('/')) return '/projets';
  if (path.startsWith('//') || path.startsWith('/\\')) return '/projets';
  return path;
}

/**
 * Le jeton d'invitation ne doit pas voyager en clair dans le `state` OAuth (audit sécurité,
 * revue du lot 2) : `state` ne porte qu'une clé aléatoire, `returnTo` lui-même est déposé en
 * `sessionStorage` sous cette clé et relu (une seule fois) par `initAuth()`.
 */
const RETURN_TO_PREFIX = 'exiplan.returnTo.';

function randomKey() {
  return `${RETURN_TO_PREFIX}${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function storeReturnTo(path) {
  const key = randomKey();
  try {
    sessionStorage.setItem(key, path);
  } catch {
    // sessionStorage indisponible (navigation privée durcie…) : le retour post-connexion sera
    // simplement absent, jamais une exception qui casserait la connexion elle-même.
    return null;
  }
  return key;
}

/** Lit puis efface la valeur mémorisée — à consommer une seule fois, jamais un repli général. */
function consumeReturnTo(key) {
  if (typeof key !== 'string' || !key.startsWith(RETURN_TO_PREFIX)) return null;
  try {
    const value = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return value;
  } catch {
    return null;
  }
}

let initPromise = null;
/** Résultat du retour de connexion, consommé une seule fois par `initAuth()` (§ correctif). */
let pendingReturnTo = null;
let pendingError = null;

/**
 * Initialise MSAL et termine un éventuel retour de `loginRedirect()`.
 *
 * Idempotent (mémoïsé) : l'initialisation MSAL elle-même ne se fait qu'une fois, mais le
 * `returnTo`/l'erreur qui en résultent ne sont rendus **qu'au premier appel** — les appels
 * suivants reçoivent `{ returnTo: null, error: null }`. C'est ce qui empêche un rebond de
 * navigation si `initAuth()` est rappelé (effet ré-exécuté, second montage…) : sans cette
 * consommation, le même `returnTo` serait renvoyé et re-naviguerait à chaque fois.
 * @returns {Promise<{ returnTo: string|null, error: unknown|null }>}
 */
export function initAuth() {
  if (!msalInstance) return Promise.resolve({ returnTo: null, error: null });
  if (!initPromise) {
    initPromise = msalInstance
      .initialize()
      .then(() => msalInstance.handleRedirectPromise())
      .then((result) => {
        const account = result?.account ?? msalInstance.getAllAccounts()[0] ?? null;
        if (account) msalInstance.setActiveAccount(account);
        pendingReturnTo = result?.state ? consumeReturnTo(result.state) : null;
      })
      .catch((error) => {
        pendingError = error;
      });
  }
  return initPromise.then(() => {
    const returnTo = pendingReturnTo;
    const error = pendingError;
    pendingReturnTo = null;
    pendingError = null;
    return { returnTo, error };
  });
}

/**
 * Lance une connexion interactive (redirection plein écran). `returnTo` est déposé en
 * `sessionStorage` sous une clé aléatoire ; seule cette clé voyage dans `state` (pas le
 * jeton d'invitation en clair, § correctif ci-dessus).
 *
 * `prompt: 'login'` force une ré-authentification même si la session CIAM du navigateur est
 * encore valide — utilisé pour un geste qui exige une connexion récente
 * (`requireRecentReauth` serveur, 401 `REAUTH_REQUIRED`) : un jeton juste renouvelé en
 * silence ne rajeunit pas `auth_time`, seule une interaction le fait.
 * @param {string} [returnTo]
 * @param {{ prompt?: 'login' }} [options]
 */
export function login(returnTo = '/projets', { prompt } = {}) {
  if (!msalInstance) return Promise.resolve();
  const key = storeReturnTo(sanitizeReturnTo(returnTo));
  return msalInstance.loginRedirect({
    scopes: SCOPES,
    ...(key ? { state: key } : {}),
    ...(prompt ? { prompt } : {}),
  });
}

/** Canal de diffusion multi-onglets (§ correctif « déconnexion multi-onglets »). */
const authChannel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('exiplan-auth') : null;

/** Déconnexion MSAL : le serveur n'a plus de cookie à effacer pour ce mode (D3, D9). */
export function logout() {
  authChannel?.postMessage({ type: 'logout' });
  if (!msalInstance) return Promise.resolve();
  return msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
}

/**
 * Prévient les autres onglets qu'une déconnexion a eu lieu dans celui-ci : chacun efface le
 * cache MSAL (compte actif, jetons) et repasse en anonyme, sans attendre son propre
 * renouvellement de jeton pour s'en apercevoir.
 * @param {() => void} handler
 * @returns {() => void} désabonnement
 */
export function onExternalLogout(handler) {
  if (!authChannel) return () => {};
  const listener = (event) => {
    if (event?.data?.type === 'logout') handler();
  };
  authChannel.addEventListener('message', listener);
  return () => authChannel.removeEventListener('message', listener);
}

/** Vrai si un compte MSAL est actif — sert à `lib/api.js` pour ne jamais laisser le cookie
 *  legacy réauthentifier une autre identité qu'un compte porteur actif (audit sécurité). */
export function hasActiveAccount() {
  return Boolean(msalInstance?.getActiveAccount());
}

/**
 * Jeton d'accès courant, ou `null` si aucun compte actif, ou si une interaction est requise
 * (`InteractionRequiredAuthError`) — c'est alors à l'appelant de décider (relance interactive,
 * ou constat d'échec) : ce module ne redirige jamais tout seul depuis un simple renouvellement
 * silencieux. Toute autre erreur MSAL est propagée telle quelle.
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<string|null>}
 */
export async function getAccessToken({ forceRefresh = false } = {}) {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: SCOPES, account, forceRefresh });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) return null;
    throw error;
  }
}
